/**
 * One capture session: its files, and the analysis that can be run on them.
 *
 * The media list shows metadata only. There is no thumbnail because the API
 * serves no bytes - `GET /media/{id}` returns a record, not a file - and there
 * is no `stored_filename` in the response to build a path from even if one
 * wanted to. Inventing `${base}/data/uploads/${name}` would produce broken
 * images at best and a path disclosure at worst, so the card says plainly that
 * previews are not available yet.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { assignGuidedCoverageRegion, estimateProjectRegions, finalizeCaptureSession, getCaptureSession, getProject, getProjectWorkflow, getReconstructionReadiness, getSessionMediaSummary, getSessionQualitySummary, listSessionMedia } from "@/api/client";
import type { MediaAsset, SubjectType } from "@/api/types";
import { Button, Callout, DataList, EmptyState, Loading, StatusBadge } from "@/components/ui";
import { ErrorState } from "@/components/ErrorState";
import {
  CAPTURE_PURPOSE_LABELS,
  CAPTURE_SESSION_STATUS_LABELS,
  CAPTURE_SESSION_STATUS_MEANING,
  CAPTURE_SESSION_STATUS_TONES,
  CAPTURE_TYPE_LABELS,
  MEDIA_STATUS_LABELS,
  MEDIA_STATUS_TONES,
  MEDIA_TYPE_LABELS,
} from "@/i18n/terms";
import {
  formatBytes,
  formatDateTime,
  formatDimensions,
  formatDuration,
} from "@/lib/format";
import { useAction, useAsync } from "@/lib/useAsync";
import { paths } from "@/app/paths";
import { AnalysisCenter } from "./AnalysisCenter";
import { AnalysisSteps } from "./AnalysisSteps";
import { DeleteMediaButton } from "./DeleteMediaButton";
import { MediaPreview } from "./MediaPreview";
import { MediaCountTruth } from "./MediaCountTruth";
import { MediaUpload, type CaptureExpectation } from "./MediaUpload";
import { analysisOf, useMediaAnalyses } from "./useMediaAnalyses";
import { eligibleFor } from "./restorationActions";
import { verdictOf, type MediaAnalysis } from "./mediaAnalysis";
import { MediaPreviewDialog } from "./MediaPreviewDialog";
import { BulkMediaActions } from "./BulkMediaActions";
import "./sessions.css";
import { GUIDED_REGIONS, guidedRegionLabel } from "@/features/coverage/guidedCoverage";

export function SessionScreen() {
  const { sessionId = "" } = useParams<{ sessionId: string }>();

  const sessionFetcher = useCallback(
    (signal: AbortSignal) => getCaptureSession(sessionId, { signal }),
    [sessionId],
  );
  const { state: session, reload: reloadSession } = useAsync(sessionFetcher);

  const projectFetcher = useCallback(
    (signal: AbortSignal) =>
      session.data
        ? getProject(session.data.project_id, { signal })
        : Promise.resolve(null),
    [session.data],
  );
  const { state: project } = useAsync(projectFetcher);

  const mediaFetcher = useCallback(
    (signal: AbortSignal) => listSessionMedia(sessionId, { limit: 100, offset: 0 }, { signal }),
    [sessionId],
  );
  const { state: media, reload: reloadMedia } = useAsync(mediaFetcher);
  const summaryFetcher = useCallback(
    (signal: AbortSignal) => getSessionMediaSummary(sessionId, { signal }),
    [sessionId],
  );
  const { state: mediaSummary, reload: reloadSummary } = useAsync(summaryFetcher);
  const [additionalMedia,setAdditionalMedia]=useState<MediaAsset[]>([]);
  /**
   * The session's verdicts, counted server-side.
   *
   * Fetched alongside the page rather than derived from it: `assets` is
   * whatever has been loaded so far, and the analysis summary is a statement
   * about the capture, not about the scroll position.
   */
  const qualitySummaryFetcher = useCallback(
    (signal: AbortSignal) => getSessionQualitySummary(sessionId, { signal }),
    [sessionId],
  );
  const { state: qualitySummary, reload: reloadQualitySummary } = useAsync(qualitySummaryFetcher);

  const assets=useMemo(()=>[...(media.data ?? []),...additionalMedia],[media.data,additionalMedia]);
  const loadMore=useAction(useCallback(
    () => listSessionMedia(sessionId,{limit:100,offset:assets.length}),
    [sessionId,assets.length],
  ));

  /**
   * The quality assessments, read once for the whole screen.
   *
   * The cards, the analysis centre and the analysis chain all report the same
   * verdicts. Read separately, that was two requests per file before the cards
   * showed anything at all.
   */
  const mediaIds = useMemo(()=>assets.map((asset)=>asset.id),[assets]);
  const quality = useMediaAnalyses(mediaIds);

  /*
   * Readiness and the project workflow are read here rather than inside the
   * analysis centre, for two reasons. They change when media changes - keyed
   * only on the project id, the reconstruction line kept saying "5 görüntü
   * girebilir" after twenty more had been uploaded - and the upload box needs
   * the same numbers to say how many photographs this project expects.
   */
  const projectId = session.data?.project_id ?? null;
  const readinessFetcher = useCallback(
    (signal: AbortSignal) => projectId ? getReconstructionReadiness(projectId, { signal }) : Promise.resolve(null),
    [projectId],
  );
  const { state: readiness, reload: reloadReadiness } = useAsync(readinessFetcher);
  const workflowFetcher = useCallback(
    (signal: AbortSignal) => projectId ? getProjectWorkflow(projectId, { signal }) : Promise.resolve(null),
    [projectId],
  );
  const { state: workflow, reload: reloadWorkflow } = useAsync(workflowFetcher);

  /**
   * One refresh for the whole screen.
   *
   * Everything on it describes the same set of files, so anything that
   * changes that set - an upload, a delete, a region assignment, a finished
   * analysis - has to re-read all of it. Reloading only the list is what left
   * the counts, the verdicts and the reconstruction line each describing a
   * different moment.
   */
  const refresh = useCallback(()=>{
    setAdditionalMedia([]);
    reloadSession();
    reloadMedia();
    reloadSummary();
    reloadQualitySummary();
    quality.reload();
    reloadReadiness();
    reloadWorkflow();
  },[reloadSession,reloadMedia,reloadSummary,reloadQualitySummary,quality,reloadReadiness,reloadWorkflow]);

  /**
   * Region estimation, run for the customer rather than asked for.
   *
   * The requirement is that a photograph gets looked at without anybody
   * pressing anything. The estimator existed and was never called, so the
   * honest description of the product was that every angle stayed unmarked
   * until a person marked it.
   *
   * **The server decides whether there is anything to do.** An earlier version
   * worked it out here - is some photograph unestimated, are there at least
   * two known ones to compare against - and it was wrong twice over. Those
   * predicates are computed from the loaded *page*, which is the same mistake
   * this screen was just fixed for elsewhere, and a page served from cache
   * made the answer stale: the request then never went out at all, silently.
   *
   * So the condition is only "this screen is showing a project". The endpoint
   * is cheap when there is nothing to do - it answers `anchors_missing` in
   * about half a second without encoding anything - and it is idempotent by
   * construction, because a model's write is refused onto anything a person
   * settled. `estimated` is a ref so one mount makes one request.
   */
  const estimated = useRef<string | null>(null);
  const [estimating, setEstimating] = useState(false);

  /**
   * Ask the server for suggestions, at most one request in flight.
   *
   * Extracted from the mount effect because an upload has to be able to ask
   * again. The mount guard alone meant photographs added after arriving on
   * this screen never got a suggestion at all: the estimator had already run
   * for this project, so the card under a freshly uploaded file offered six
   * angles and no hint, and the shortlist only appeared if somebody left the
   * screen and came back. Which nobody does.
   */
  const requestEstimates = useCallback(() => {
    if (!projectId) return;
    setEstimating(true);
    void estimateProjectRegions(projectId)
      .then((report) => {
        // Only re-read the screen when something actually changed. A visit
        // that estimated nothing must not cost a full refresh.
        if (report.considered > 0) refresh();
      })
      // A failure here costs a suggestion, never a photograph. The angles are
      // still assignable by hand, which is what they were before this existed.
      .catch(() => undefined)
      .finally(() => setEstimating(false));
  }, [projectId, refresh]);

  useEffect(() => {
    if (!projectId || estimated.current === projectId) return;
    estimated.current = projectId;
    requestEstimates();
  }, [projectId, requestEstimates]);

  /**
   * After an upload: refresh the list, then ask again for the new files.
   *
   * In that order and not the other way round. The estimator reads what the
   * server holds, so it does not need the refresh to have landed - but the
   * grid does, and a suggestion arriving before the card it belongs to has
   * nothing to attach itself to.
   */
  const afterUpload = useCallback(() => {
    refresh();
    requestEstimates();
  }, [refresh, requestEstimates]);

  const finalizing = useAction(
    useCallback(() => finalizeCaptureSession(sessionId), [sessionId]),
  );

  /**
   * Which file the large viewer is showing, or null when it is closed.
   *
   * An index into the loaded list rather than an id, because the viewer's
   * previous/next are positions in what the user is looking at. The list can
   * shrink under it - deleting from inside the viewer is the usual way - and
   * the viewer closes itself when its index no longer names a file.
   */
  const [viewing, setViewing] = useState<number | null>(null);

  /**
   * The files the reader has ticked.
   *
   * Ids rather than indices, so a reload that reorders or shortens the list
   * cannot silently move the selection onto different files. Ids that no
   * longer exist are dropped below rather than kept as a set that quietly
   * disagrees with the screen.
   */
  const [selection, setSelection] = useState<ReadonlySet<string>>(new Set());
  const selected = useMemo(
    () => assets.filter((asset) => selection.has(asset.id)).map((asset) => asset.id),
    [assets, selection],
  );
  /**
   * The ticked files the enhancement pipeline would actually act on.
   *
   * Decided from the same stored assessments the analysis screen counts, so
   * "12 blurry photographs" and "12 files this can help" are the same twelve
   * rather than two numbers that can disagree. A selection with none of them
   * still shows the action, with the reason - a reader who ticked twelve files
   * and sees no button learns nothing.
   */
  const enhanceable = useMemo(
    () =>
      eligibleFor(
        "enhance",
        assets
          .filter((asset) => selection.has(asset.id))
          .map((asset) => ({ asset, analysis: analysisOf(quality, asset.id) })),
      ),
    [assets, selection, quality],
  );
  const clearSelection = useCallback(() => setSelection(new Set()), []);
  // A different session is a different set of files. Nothing carries over.
  useEffect(() => clearSelection(), [sessionId, clearSelection]);
  const toggle = useCallback((id: string, on: boolean) => {
    setSelection((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  if (session.status === "error") {
    return <ErrorState error={session.error} onRetry={reloadSession} />;
  }
  if (session.data === null) return <Loading label="Çekim yükleniyor" />;

  const current = session.data;

  /*
   * Whether the subject analysis has actually run - not whether it could.
   * `null` while the workflow is unread, which the analysis centre renders as
   * "henüz ölçülmedi" rather than as a verdict.
   */
  const headStep = workflow.data?.steps.find((step) => step.key === "head_analysis");
  const headAnalysed = headStep ? headStep.state === "done" : null;

  /*
   * What the upload box may say about photograph counts. Only the readiness
   * endpoint's own numbers; null until it has answered, because a guessed
   * target is a quality promise nobody measured.
   */
  const expectation: CaptureExpectation | null = readiness.data
    ? {
        photos: readiness.data.photo_count,
        minimum: readiness.data.minimum_required,
        maximum: readiness.data.maximum_allowed,
      }
    : null;

  return (
    <div className="stack">
      <div className="stack-tight">
        <Link to={paths.projectSessions(current.project_id)} className="muted">
          ← Çekimler
        </Link>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "start" }}>
          <h1>
            {CAPTURE_PURPOSE_LABELS[current.purpose]} ·{" "}
            {CAPTURE_TYPE_LABELS[current.capture_type]}
          </h1>
          <StatusBadge tone={CAPTURE_SESSION_STATUS_TONES[current.status]}>
            {CAPTURE_SESSION_STATUS_LABELS[current.status]}
          </StatusBadge>
        </div>
        <p className="muted">Oluşturulma {formatDateTime(current.created_at)}</p>
        {current.notes && <p>{current.notes}</p>}
      </div>

      {/* The lifecycle state, explained rather than hidden. A badge reading
          "Sonlandırılmadı" with no way to act on it would be the cosmetic fix
          this replaced. */}
      <Callout tone={current.status === "complete" ? "success" : "neutral"}>
        <div className="stack-tight">
          <span>{CAPTURE_SESSION_STATUS_MEANING[current.status]}</span>
          {/*
            Finishing is offered only where there is something to finish. An
            empty session showed the same primary button as a full one, and
            pressing it closed a capture the user had not made yet.
          */}
          {current.status === "pending" && assets.length > 0 && (
            <div>
              <Button
                busy={finalizing.busy}
                onClick={() => {
                  void finalizing.run().then((s) => {
                    if (s) reloadSession();
                  });
                }}
              >
                Çekimi sonlandır
              </Button>
            </div>
          )}
          {current.status === "pending" && media.data !== null && assets.length === 0 && (
            <span className="muted">
              Sonlandırılacak bir dosya yok. Önce bu oturuma en az bir fotoğraf yükleyin.
            </span>
          )}
          {finalizing.error && <ErrorState error={finalizing.error} compact />}
        </div>
      </Callout>

      <section className="stack">
        <h2>Dosya yükle</h2>
        {project.data && <MediaUpload sessionId={sessionId} subjectType={project.data.subject_type} onUploaded={afterUpload} expectation={expectation} />}
      </section>

      <section className="stack">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h2>Yüklenen dosyalar</h2>
          {mediaSummary.data && <span className="muted">{mediaSummary.data.total} dosya · {mediaSummary.data.photos} fotoğraf · {mediaSummary.data.videos} video</span>}
          {/*
            What the estimator is doing, and then what it found. Both are real
            counts of stored rows - nothing here is a prediction of how many it
            will get right, because it is not permitted to decide any of them.
          */}
          {estimating && <span className="muted">Bölgeler belirleniyor…</span>}
          {/*
            The session's own count, from the same endpoint as the totals
            beside it - so it is right on the visit that ran the estimate and
            on every visit afterwards. Counting the loaded array said "88" for
            a session with 138 awaiting review.
          */}
          {!estimating && (mediaSummary.data?.region_review_required ?? 0) > 0 && (
            <span className="muted">
              {mediaSummary.data?.region_review_required} fotoğraf için çekim açısı
              kontrolü gerekiyor
              {/*
                A count nobody can act on is a count nobody reads. This is the
                link from the number to the queue that works through it - one
                photograph at a time with the shortlist as buttons, rather than
                a hundred and fifty dropdowns on this screen.
              */}
              {project.data && (
                <>
                  {" · "}
                  <Link to={paths.projectRegionReview(project.data.id)}>
                    Açı kontrolüne git
                  </Link>
                </>
              )}
            </span>
          )}
        </div>

        {media.status === "loading" && media.data === null && <Loading />}
        {media.status === "error" && (
          <ErrorState error={media.error} onRetry={reloadMedia} compact />
        )}
        {/*
          Selecting several files, because every correction in the walkthrough
          - delete these six, mark those twelve as the left side - was one card
          at a time. "Tümünü seç" is scoped to what is loaded and says so: a
          session can hold more files than this page has asked for, and a
          control that claimed to select 212 while holding 100 would be lying
          about what the next button does.
        */}
        {assets.length > 0 && (
          <div className="row bulkbar__select">
            <label className="bulkbar__checkbox">
              <input
                type="checkbox"
                checked={selected.length === assets.length}
                // A partial selection is neither on nor off.
                ref={(node) => {
                  if (node) node.indeterminate = selected.length > 0 && selected.length < assets.length;
                }}
                onChange={(event) =>
                  setSelection(
                    event.target.checked ? new Set(assets.map((asset) => asset.id)) : new Set(),
                  )
                }
              />
              <span>Tümünü seç ({assets.length})</span>
            </label>
            <span className="faint">Yalnızca bu sayfada yüklü olanlar</span>
          </div>
        )}

        {project.data && (
          <BulkMediaActions
            projectId={project.data.id}
            selected={selected}
            subjectType={project.data.subject_type}
            enhanceable={enhanceable}
            onClear={clearSelection}
            onCompleted={(changed) => {
              setSelection((current) => {
                const next = new Set(current);
                for (const id of changed) next.delete(id);
                return next;
              });
              refresh();
            }}
          />
        )}

        {media.data !== null &&
          (assets.length === 0 ? (
            <EmptyState title="Henüz dosya yok" description="Yukarıdan fotoğraf veya video yükleyin." />
          ) : (
            <ul className="medialist">
              {assets.map((asset, index) => (
                <MediaCard
                  key={asset.id}
                  asset={asset}
                  subjectType={project.data?.subject_type ?? "human_head"}
                  analysis={analysisOf(quality, asset.id)}
                  analysisKnown={!quality.loading}
                  selected={selection.has(asset.id)}
                  onSelect={(on) => toggle(asset.id, on)}
                  onOpen={() => setViewing(index)}
                  onChanged={refresh}
                />
              ))}
            </ul>
          ))}
        {mediaSummary.data && assets.length > 0 && (
          <MediaCountTruth
            summary={mediaSummary.data}
            loaded={assets.length}
            loadingMore={loadMore.busy}
            onLoadMore={() =>
              void loadMore.run().then((page) => {
                if (page) setAdditionalMedia((loaded) => [...loaded, ...page]);
              })
            }
          />
        )}
      </section>

      {/*
        The reminder, and the sentence it deliberately does not say.
        `finalize` is a real action - `POST /capture-sessions/{id}/finalize`
        moves PENDING to COMPLETE - but **nothing downstream reads that
        status**: reconstruction skips only FAILED sessions and analysis never
        consults it at all. So this cannot claim that finishing makes analysis
        or reconstruction use "the right data set". It says the true, smaller
        thing.

        Shown only where it is actionable: a session still pending, that has
        media in it. A completed session is never told to finish, and an empty
        one has nothing to finish.
      */}
      {current.status === "pending" && assets.length > 0 && (
        <Callout tone="neutral" title="Çekimi bitirmeyi unutmayın">
          Yeni medya eklemeyi tamamladığınızda çekim oturumunu bitirebilirsiniz.
          Bitirilen bir oturum, listede tamamlandı olarak görünür.
        </Callout>
      )}

      {/* Results first, then the controls that produce them. Reading what was
          measured is the more frequent visit; starting a run is the rarer one. */}
      {project.status === "success" && project.data !== null && assets.length > 0 && (
        <AnalysisCenter
          project={project.data}
          media={assets}
          sessionQuality={qualitySummary.data}
          // The same two words the header above uses, so a row in the
          // drill-down names the session the reader is already looking at.
          sessionLabel={`${CAPTURE_PURPOSE_LABELS[current.purpose]} · ${
            CAPTURE_TYPE_LABELS[current.capture_type]
          }`}
          quality={quality}
          readiness={readiness.data ?? null}
          headAnalysed={headAnalysed}
          onMediaChanged={refresh}
        />
      )}

      <AnalysisSteps
        sessionId={sessionId}
        media={assets}
        mediaTotals={mediaSummary.data}
        qualitySummary={qualitySummary.data}
        quality={quality}
        onCompleted={refresh}
        headAnalysis={project.status === "success" && project.data !== null
          ? (project.data.capabilities?.head_analysis ?? project.data.subject_type === "human_head")
          : false}
        analysisKind={project.status === "success" && project.data !== null
          ? project.data.perception_profile?.analysis_kind ?? (project.data.subject_type === "human_head" ? "human_head" : "spatial")
          : "spatial"}
      />
      {viewing !== null && (
        <MediaPreviewDialog
          items={assets.map((asset) => ({ asset, analysis: analysisOf(quality, asset.id) }))}
          index={viewing}
          subjectType={project.data?.subject_type ?? "human_head"}
          /*
            The session's real count, not the loaded page. Without it the
            dialog opened on a hundred-and-fifty-file session saying "1 / 100",
            which reads as fifty files having been lost. Undefined until the
            summary answers, and undefined means the dialog falls back to what
            it has rather than guessing a total.
          */
          total={mediaSummary.data?.total}
          loadingMore={loadMore.busy}
          onNeedMore={() =>
            void loadMore.run().then((page) => {
              if (page) setAdditionalMedia((loaded) => [...loaded, ...page]);
            })
          }
          onIndex={setViewing}
          onClose={() => setViewing(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

function MediaCard({ asset, subjectType, analysis, analysisKnown, selected, onSelect, onOpen, onChanged }: {
  asset: MediaAsset;
  subjectType: SubjectType;
  /** What the analysis says about this one file. */
  analysis: MediaAnalysis;
  /** False while the verdicts are still being read: nothing is claimed yet. */
  analysisKnown: boolean;
  selected: boolean;
  onSelect: (on: boolean) => void;
  onOpen: () => void;
  onChanged: () => void;
}) {
  const isVideo = asset.media_type === "video";
  const [assigning,setAssigning]=useState(false);
  /**
   * The estimator's best answer for this photograph, when it has one this
   * build can name. A station the server proposes and the frontend map has no
   * label for is dropped rather than shown as its raw key - the leak this
   * release already closed twice.
   */
  const suggestion = (() => {
    const key = asset.guided_region_alternate;
    if (!key || subjectType === "human_head") return null;
    const label = guidedRegionLabel(subjectType, key);
    return label ? { key, label } : null;
  })();
  const [assignmentError,setAssignmentError]=useState(false);
  const verdict = verdictOf(analysis);
  return (
    <li className={selected ? "card mediacard mediacard--selected" : "card mediacard"}>
      <label className="bulkbar__checkbox">
        <input
          type="checkbox"
          checked={selected}
          onChange={(event) => onSelect(event.target.checked)}
        />
        {/* The filename, so the announced name says which file is being
            ticked rather than "checkbox" forty times. */}
        <span className="visually-hidden">{asset.original_filename} dosyasını seç</span>
      </label>

      {/*
        The picture opens the file. Finding the bad photograph used to mean
        reading the analysis screen, then finding the same filename back here.
      */}
      <button
        type="button"
        className="mediacard__open"
        onClick={onOpen}
        aria-label={`${asset.original_filename} dosyasını büyüt`}
      >
        <MediaPreview asset={asset} />
      </button>

      <span className="mediacard__name">{asset.original_filename}</span>

      <div className="row" style={{ justifyContent: "space-between" }}>
        <span className="faint">{MEDIA_TYPE_LABELS[asset.media_type]}</span>
        <StatusBadge tone={MEDIA_STATUS_TONES[asset.status]}>
          {MEDIA_STATUS_LABELS[asset.status]}
        </StatusBadge>
      </div>

      {/*
        The verdict, on the file it is about. It lived only in the analysis
        screen's drill-down, so a user who had just run an analysis still could
        not tell which of their photographs was the problem.

        Nothing is shown until the read finishes: a card that says "Analiz
        edilmedi" for a second and then changes its mind is worse than one that
        waits.
      */}
      {analysisKnown && (
        <div>
          <StatusBadge tone={verdict.tone}>
            <span aria-hidden="true">{verdict.glyph}</span> {verdict.label}
          </StatusBadge>
        </div>
      )}

      <DataList
        items={[
          { term: "Boyut", value: formatBytes(asset.size_bytes) },
          { term: "Çözünürlük", value: formatDimensions(asset.width, asset.height) },
          ...(isVideo
            ? [
                { term: "Süre", value: formatDuration(asset.duration_seconds) },
                { term: "Kare", value: asset.frame_count ? String(asset.frame_count) : "—" },
              ]
            : []),
        ]}
      />

      {subjectType !== "human_head" && (
        <>
          <label className="field">
            <span>Çekim açısı</span>
            <select
              value={asset.guided_coverage_region ?? ""}
              disabled={assigning}
              onChange={(event) => {
                setAssigning(true);
                setAssignmentError(false);
                void assignGuidedCoverageRegion(asset.id, event.target.value || null)
                  .then(onChanged)
                  .catch(() => setAssignmentError(true))
                  .finally(() => setAssigning(false));
              }}
            >
              <option value="">{asset.guided_region_review_state === "review_required" ? "Kontrol gerekli" : "İşaretlenmedi"}</option>
              {GUIDED_REGIONS[subjectType].map((region) => (
                <option key={region.key} value={region.key}>
                  {region.label}
                </option>
              ))}
            </select>
            {/*
              The estimator's answer, where it has one, shown as a suggestion
              and never as the value of the field above. It ran, it was not
              confident enough to decide, and it narrowed six choices to two -
              which is the whole of what it is allowed to contribute while the
              subject's benchmark sits below the release gate.
            */}
            {asset.guided_region_review_state === "auto_assigned" || asset.guided_region_review_state === "tentative" ? (
              /*
                The model wrote this one. Named as the model's work rather
                than left to look like something the customer chose, because
                the measurement behind the threshold is not a 95% one and a
                wrong angle that nobody can see is a wrong angle the coverage
                aggregate counts as evidence. The select above is the
                correction, and a person's choice outranks this permanently -
                `RegionSource.MANUAL` beats `VISION_MODEL` and no later run
                may write over it.
              */
              <small className="muted">
                {asset.guided_region_review_state === "tentative" ? "Tahmini bölge — doğrulanmadı" : "Bu açıyı yapay zekâ belirledi"}
                {typeof asset.guided_region_confidence === "number"
                  ? ` (güven %${Math.round(asset.guided_region_confidence * 100)})`
                  : ""}
                . Yanlışsa yukarıdan değiştirin; sizin seçiminiz kalıcıdır.
              </small>
            ) : asset.guided_region_review_state === "review_required" ? (
              <div className="stack-tight">
                {/*
                  One press to take the suggestion, because reading it in a
                  sentence and then hunting for the same word in a six-option
                  select is the whole cost of not deciding automatically - and
                  that cost was landing on the customer rather than on the
                  model. The model still writes nothing: pressing this is a
                  person's assignment, carrying the same authority as picking
                  from the list, and no later run may overwrite it.
                */}
                {suggestion && (
                  <div className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
                    <span className="muted">Öneri: {suggestion.label}</span>
                    <Button
                      variant="secondary"
                      disabled={assigning}
                      ariaLabel={`Önerilen açıyı kullan: ${suggestion.label}`}
                      onClick={() => {
                        setAssigning(true);
                        setAssignmentError(false);
                        void assignGuidedCoverageRegion(asset.id, suggestion.key)
                          .then(onChanged)
                          .catch(() => setAssignmentError(true))
                          .finally(() => setAssigning(false));
                      }}
                    >
                      Bunu kullan
                    </Button>
                  </div>
                )}
                <small className="muted">
                  Ölçülen doğruluk otomatik işaretleme için gereken düzeye
                  ulaşmadı; bu yüzden öneri uygulanmaz, kararı siz verirsiniz.
                </small>
              </div>
            ) : (
              <small className="muted">
                Bu fotoğrafın hangi açı için çekildiğini siz belirlersiniz; ölçülen bir
                sonuç değildir.
              </small>
            )}
          </label>
          {assignmentError && (
            <Callout tone="danger">Çekim açısı işaretlenemedi. Tekrar deneyin.</Callout>
          )}
        </>
      )}

      {/*
        Deleting is one of the things a file offers, not the thing. It ran the
        full width of the card as the only control on it, which made "remove
        this" the most prominent action a photograph had.
      */}
      <div className="mediacard__actions">
        <Button onClick={onOpen}>Büyüt</Button>
        <DeleteMediaButton asset={asset} onDeleted={onChanged} />
      </div>
    </li>
  );
}
